import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "onChange", "label", "disabled", "fullWidth", "sx", "slotProps", "open", "onOpen", "onClose", "digital"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { DesktopTimePicker as MuiDesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { useSelector } from 'react-redux';
import { translate as translateConstructor } from '@haldor/ui';
import { getNormalizedActiveLanguage, getPickersLocale } from 'helpers/locale';
import { usePickerAutoClose } from './usePickerAutoClose';
import GlobalStyles from '@mui/material/GlobalStyles';

// Generate unique ID for each TimePicker instance
var timePickerIdCounter = 0;

/**
 * TimePicker
 *
 * Reusable MUI TimePicker wrapper that handles LocalizationProvider,
 * AdapterMoment, and locale text automatically.
 *
 * Replaces the legacy custom time_picker.js (plain HTML inputs) with a
 * fully accessible MUI-based time picker.
 *
 * Props:
 * - value: Moment | null  (equivalent to the old `time` prop)
 * - onChange: (momentValue) => void
 * - label: string (optional)
 * - disabled: boolean (optional)
 * - fullWidth: boolean (optional, default true)
 * - sx: object (optional)
 * - slotProps: object (optional, merged with fullWidth default)
 * - open, onOpen, onClose: optional controlled props (if not provided, auto-close is enabled)
 * - ...rest: any additional MUI TimePicker props
 */
var TimePicker = function TimePicker(/** @type {any} */_ref) {
  var value = _ref.value,
    onChange = _ref.onChange,
    label = _ref.label,
    disabled = _ref.disabled,
    _ref$fullWidth = _ref.fullWidth,
    fullWidth = _ref$fullWidth === void 0 ? true : _ref$fullWidth,
    sx = _ref.sx,
    slotProps = _ref.slotProps,
    open = _ref.open,
    onOpen = _ref.onOpen,
    onClose = _ref.onClose,
    _ref$digital = _ref.digital,
    digital = _ref$digital === void 0 ? true : _ref$digital,
    rest = _objectWithoutProperties(_ref, _excluded);
  var translations = useSelector(function (state) {
    var _state$Languages;
    return /** @type {any} */(_state$Languages = state.Languages) === null || _state$Languages === void 0 ? void 0 : _state$Languages.translations;
  });
  var translate = React.useMemo(function () {
    return translateConstructor(translations);
  }, [translations]);
  var normalizedLang = React.useMemo(function () {
    return getNormalizedActiveLanguage();
  }, [translations]);
  var pickersLocale = React.useMemo(function () {
    return getPickersLocale(normalizedLang);
  }, [normalizedLang]);
  var localeText = React.useMemo(function () {
    var _pickersLocale$compon;
    return pickersLocale === null || pickersLocale === void 0 || (_pickersLocale$compon = pickersLocale.components) === null || _pickersLocale$compon === void 0 || (_pickersLocale$compon = _pickersLocale$compon.MuiLocalizationProvider) === null || _pickersLocale$compon === void 0 || (_pickersLocale$compon = _pickersLocale$compon.defaultProps) === null || _pickersLocale$compon === void 0 ? void 0 : _pickersLocale$compon.localeText;
  }, [pickersLocale]);
  var effectiveLabel = label ? translate(label) : undefined;
  var PickerComponent = digital ? MuiDesktopTimePicker : MuiTimePicker;
  var TOP_Z_INDEX = 200000;
  var mergedSlotProps = _objectSpread(_objectSpread({
    textField: _objectSpread(_objectSpread({
      fullWidth: fullWidth
    }, slotProps && slotProps.textField || {}), {}, {
      sx: _objectSpread(_objectSpread({}, slotProps && slotProps.textField && slotProps.textField.sx || {}), {}, {
        '& .MuiInputBase-root': _objectSpread({
          backgroundColor: '#fff'
        }, slotProps && slotProps.textField && slotProps.textField.sx && slotProps.textField.sx['& .MuiInputBase-root'] || {})
      })
    })
  }, slotProps), {}, {
    popper: _objectSpread(_objectSpread({}, slotProps && slotProps.popper || {}), {}, {
      sx: _objectSpread(_objectSpread({}, slotProps && slotProps.popper && slotProps.popper.sx || {}), {}, {
        zIndex: TOP_Z_INDEX,
        fontSize: '1rem',
        transform: 'none',
        maxWidth: 'none',
        '.MuiClock-root, .MuiClockNumber-root, .MuiPickersSlideTransition-root': {
          fontSize: '1rem'
        }
      })
    }),
    dialog: _objectSpread(_objectSpread({}, slotProps && slotProps.dialog || {}), {}, {
      PaperProps: _objectSpread(_objectSpread({}, slotProps && slotProps.dialog && slotProps.dialog.PaperProps || {}), {}, {
        sx: _objectSpread(_objectSpread({}, slotProps && slotProps.dialog && slotProps.dialog.PaperProps && slotProps.dialog.PaperProps.sx || {}), {}, {
          zIndex: TOP_Z_INDEX,
          fontSize: '1rem',
          transform: 'none',
          maxWidth: 'none'
        })
      })
    })
  });

  // Use auto-close hook if no controlled props provided
  var pickerIdRef = React.useRef("tp_".concat(++timePickerIdCounter));
  var _usePickerAutoClose = usePickerAutoClose(pickerIdRef.current),
    _usePickerAutoClose2 = _slicedToArray(_usePickerAutoClose, 2),
    autoCloseOpen = _usePickerAutoClose2[0],
    setAutoCloseOpen = _usePickerAutoClose2[1];

  // Allow parent control OR auto-close behavior
  var isControlled = open !== undefined;
  var actualOpen = isControlled ? open : autoCloseOpen;
  var handleOpen = function handleOpen() {
    if (isControlled && onOpen) onOpen();else setAutoCloseOpen(true);
  };
  var handleClose = function handleClose() {
    if (isControlled && onClose) onClose();else setAutoCloseOpen(false);
  };
  return /*#__PURE__*/React.createElement("div", {
    id: pickerIdRef.current
  }, /*#__PURE__*/React.createElement(GlobalStyles, {
    styles: {
      '.MuiPickersPopper-root, .MuiPickersModal-root, .MuiPickersDesktopPaper-root': {
        zIndex: '1000000000 !important'
      },
      '.MuiInputAdornment-root': {
        zIndex: '1000000001'
      }
    }
  }), /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: AdapterMoment,
    adapterLocale: normalizedLang,
    localeText: localeText
  }, /*#__PURE__*/React.createElement(PickerComponent, _extends({
    value: value,
    onChange: onChange,
    label: effectiveLabel,
    disabled: disabled,
    open: actualOpen,
    onOpen: handleOpen,
    onClose: handleClose,
    sx: sx,
    slotProps: mergedSlotProps
  }, rest))));
};
export default TimePicker;